import React, {
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/router";
import { axiosApiInstance } from "../../pages/api/axios";

import Table from "./Table";
import { TableSkeleton } from "../skeletons";
import NoResults from "../NoResults/NoResults";
import {
  SUCCESS_MESSAGES,
  exportBulkActionNames,
  useSkipper,
  formatBulkActions,
} from "./utils";
import { cn, Checkbox, Tooltip, IconButton, Menu, toast } from "ui";
import { Pagination } from "../Pagination";
import { useGlobalSearchQuery } from "../GlobalSearch";
import {
  useReactTable,
  getCoreRowModel,
  type ColumnDef,
  type RowData,
  getExpandedRowModel,
  Row,
  Table as TableType,
} from "@tanstack/react-table";
import { parseErrorsToArray } from "../../utils/parseErrors";
import {
  usePaginationParams,
  type PaginationDefaultLimit,
} from "../../hooks/usePaginationParams";
import { Filters } from "../newFilters";
import { useMoneyHashQuery } from "../../hooks/useMoneyHashQuery";
import { ErrorPage } from "../ErrorPage";
import { backendKeyMap } from "../../utils/backendKeyMap";
import { snakeCaseToHumanReadable } from "../../utils/snakeCaseToHumanReadable";
import BooleanBadge from "../BooleanBadge";
import useUpdateEffect from "../../hooks/useUpdateEffect";
import ButtonActionGroup from "./ButtonActionGroup";
import {
  ChevronDownIcon,
  InformationCircleIcon,
  PlusIcon,
} from "icons/outline";
import { PageHeader } from "../PageHeading";
import AddPopOverForm from "../AddPopOverForm";
import ButtonWithConfirmModal from "./ButtonWithConfirmModal";

const booleanValues = ["true", "false", true, false];

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    className?: string;
  }
}

type TableContainerProps = {
  url: string;
  formURL?: string;
  tableTitle?: React.ReactNode;
  tableName: string;
  tableDescription?: string;
  hideFilters?: boolean;
  customRender?: Record<string, any>;
  customPreview?: Record<string, any>;
  previewMode?: "side-panel" | "expanded";
  onRowClick?: (
    rowIndex: number,
    cell: { row: { id: string } },
    preview: any
  ) => void;
  onAddItemClick?: () => void;
  onAddItemSuccessfully?: (response: any) => void;
  onEditItemClick?: (id: string, row: any, preview: any) => void;
  emptyMessage?: React.ReactNode;
  navigationTabs?: React.ReactNode;
  itemAddedSuccessfullyMessage?: string;
  BulkActionsComponent?: React.ElementType;
  customHeadings?: Record<string, string | JSX.Element>;
  customColumns?: string[];
  getPreviewPath?: (row: any) => string;
  enableRowClickNavigation?:
    | boolean
    | {
        /**
         * @default false
         */
        newTab?: boolean;
        /**
         * Can be used for tracking
         */
        callback?: (
          rowIndex: number,
          cell: { row: { id: string } },
          preview: any
        ) => void;
      };
  getRowId?: (row: any) => string;
  addItemContent?: React.ReactNode;
  hideGlobalSearch?: boolean;
  hideHeader?: boolean;
  disableBulkActions?: boolean;
  modifiedParams?: Record<string, any>;
  deleteItemEnabled?: boolean;
  onDeleteItemClick?: (id: string) => void;
  onDataLoad?: (options: {
    columnsData: any[];
    previewsData: any[];
    tableData: any[];
  }) => void;
  CustomTopButton?: React.ElementType;
  paginationDefaultLimit?: PaginationDefaultLimit;
  subComponentKey?: string;
  subComponentRender?: (row: any) => ReactNode;
  customHint?: Record<string, string>;
  tableView?: ReactNode;
  TableSummary?: React.ElementType;
  TableAlert?: React.ElementType;
  filtersProps?: {
    datePickerMode?: "daily" | "monthly";
    dateRangeMode?: "finiteIntervalDates" | "openIntervalDates";
    nonClearableFilters?: string[];
  };
  setListDetails?: (details: any) => void;
};

const TableContainer = forwardRef<HTMLDivElement, TableContainerProps>(
  (
    {
      url,
      formURL,
      tableTitle,
      tableName,
      tableDescription,
      hideFilters,
      customRender,
      customPreview,
      onRowClick,
      onAddItemClick,
      onAddItemSuccessfully,
      onEditItemClick,
      emptyMessage,
      navigationTabs,
      itemAddedSuccessfullyMessage,
      BulkActionsComponent,
      customHeadings,
      customColumns,
      getPreviewPath,
      enableRowClickNavigation,
      getRowId,
      addItemContent,
      hideGlobalSearch = false,
      hideHeader = false,
      disableBulkActions = false,
      modifiedParams = {},
      deleteItemEnabled,
      onDeleteItemClick,
      onDataLoad,
      CustomTopButton,
      paginationDefaultLimit,
      previewMode = "side-panel",
      subComponentKey = "",
      subComponentRender,
      customHint,
      tableView,
      TableSummary,
      TableAlert,
      filtersProps,
      setListDetails,
    },
    ref
  ) => {
    const router = useRouter();
    const { query } = router;
    const {
      preview: _preview,
      providerInteractionsTab: _tab,
      ...reloadingDataQuery
    } = query;
    const [paginationParams] = usePaginationParams(paginationDefaultLimit);
    const searchQuery = useGlobalSearchQuery();

    const [addOpen, setAddOpen] = useState(false);

    const [rowsData, setRowsData] = useState([]);
    const [bulkActionErrorIds, setBulkActionErrorIds] = useState<string[]>([]);
    const [autoResetSelectedRowsSkipped, skipAutoResetSelectedRows] =
      useSkipper();

    const {
      data,
      listMeta,
      count: dataCount,
      isFetching: isLoading,
      isError: isFetchingError,
      error,
      refetch,
    } = useMoneyHashQuery<any>({
      queryKey: [
        "table",
        tableName,
        url,
        { ...paginationParams, ...reloadingDataQuery, ...modifiedParams },
      ],
      queryFn: ({ signal }) =>
        axiosApiInstance
          .get(url, {
            params: {
              ...paginationParams,
              ...reloadingDataQuery,
              ...modifiedParams,
            },
            signal,
          })
          .then((response) => {
            const { tableData, previewsData } = response.data?.data?.reduce(
              (
                acc: any,
                { columns, preview }: { columns: any; preview: any }
              ) => {
                acc.tableData.push(columns);
                acc.previewsData.push(preview);
                return acc;
              },
              { tableData: [], previewsData: [] }
            );

            const orderingFields = response.data?.list_meta?.ordering_fields;

            let columnsData: ColumnDef<Record<string, any>>[] = [];

            if (tableData.length) {
              columnsData = (customColumns || Object.keys(tableData[0]))
                .map((key) => {
                  return (
                    key !== subComponentKey && {
                      id: key,
                      accessorKey: key,
                      enableSorting: orderingFields?.includes(key),
                      header: () => {
                        const headerContent =
                          customHeadings?.[key] ||
                          backendKeyMap[key] ||
                          snakeCaseToHumanReadable(key);

                        return (
                          <div className="flex flex-1 items-center gap-2">
                            {headerContent}

                            {customHint?.[key] && (
                              <div className="ml-auto">
                                <Tooltip>
                                  <Tooltip.Trigger
                                    render={<div className="cursor-pointer" />}
                                  >
                                    <InformationCircleIcon className="h-5 w-5 text-foundations-subtler" />
                                  </Tooltip.Trigger>
                                  <Tooltip.Popup
                                    variant="light"
                                    className="max-w-sm"
                                  >
                                    {customHint[key]}
                                  </Tooltip.Popup>
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        );
                      },
                      cell: ({ row }: { row: Row<any> }) => {
                        if (customRender?.[key]) {
                          return customRender[key](row.original);
                        }

                        const value = row.original[key];
                        if (booleanValues.includes(value)) {
                          return (
                            <BooleanBadge
                              value={value === "true" || value == true}
                            />
                          );
                        }

                        return value;
                      },
                    }
                  );
                })
                .filter(Boolean) as ColumnDef<Record<string, any>, any>[];
            }

            return {
              ...response.data,
              data: {
                tableData,
                columnsData,
                previewsData,
                totalCount: response.data.count,
              },
            };
          })
          .catch((error) => {
            const [message] = parseErrorsToArray(error);

            if (error.status === 400) {
              toast.error(message || "Error getting data.");

              return {};
            }
          }),
      cacheTime: 0,
      keepPreviousData: true,
    });

    const pagesCount = Math.ceil(dataCount! / +paginationParams.limit);

    const {
      columnsData,
      tableData,
      previewsData,
      filtersEnabled,
      filters,
      listMetaEnableSearch,
      addEnabled,
      editEnabled,
      deleteEnabled,
      bulkActions,
      bulkActionsPermissions,
      summary,
      listDetails,
    } = useMemo(
      () => ({
        tableData: data?.tableData || [],
        previewsData: data?.previewsData || [],
        columnsData: data?.columnsData || [],
        filtersEnabled: listMeta?.filter_enabled,
        filters: listMeta?.filter_types,
        listMetaEnableSearch: listMeta?.search_enabled,
        addEnabled: listMeta?.add_enabled,
        editEnabled: listMeta?.edit_enabled,
        deleteEnabled:
          listMeta?.bulk_action_data?.includes("delete_selected") ||
          deleteItemEnabled,
        bulkActionsPermissions: listMeta?.bulk_action_permissions || {},
        bulkActions: listMeta?.bulk_action_data
          ? formatBulkActions(listMeta?.bulk_action_data as any)
          : [],
        summary: listMeta?.summary || {},
        listDetails: listMeta?.list_details || {},
      }),
      [data, listMeta, deleteItemEnabled]
    );

    useEffect(() => {
      if (!listDetails || !setListDetails) return;

      setListDetails?.(listDetails);
    }, [listDetails, setListDetails]);

    useEffect(() => {
      if (!itemAddedSuccessfullyMessage) return;

      toast.success(itemAddedSuccessfullyMessage);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemAddedSuccessfullyMessage]);

    useEffect(() => {
      if (bulkActionErrorIds.length) {
        (skipAutoResetSelectedRows as any)();
      }
      setRowsData(tableData);
    }, [tableData, bulkActionErrorIds.length, skipAutoResetSelectedRows]);

    useUpdateEffect(() => {
      if (autoResetSelectedRowsSkipped) return;
      tableInstance.resetRowSelection(true);
    }, [tableData]);

    const onDataLoadRef = useRef(onDataLoad);
    onDataLoadRef.current = onDataLoad;

    useEffect(() => {
      if (!data) return;
      onDataLoadRef.current?.(data);
    }, [data]);

    const tableSorts: Record<string, "asc" | "desc"> = useMemo(() => {
      const ordering = router.query.ordering as string;
      if (!ordering) return {};

      if (ordering.startsWith("-"))
        return {
          [ordering.slice(1)]: "desc",
        };

      return {
        [ordering]: "asc",
      };
    }, [router.query.ordering]);

    // Update to be state if we are going to introduce control visibility
    const columnVisibility = useMemo(() => {
      return (columnsData as ColumnDef<any>[]).reduce(
        (acc, column) => {
          acc[column.id!] = !column.id?.startsWith("ui_hidden");
          return acc;
        },
        {} as Record<string, boolean>
      );
    }, [columnsData]);

    const columns = useMemo(() => {
      return [
        // Selection Column
        (bulkActions?.length || BulkActionsComponent) &&
          !disableBulkActions &&
          ({
            id: "select",
            header: ({ table }) => (
              <div>
                <Checkbox
                  checked={table.getIsAllRowsSelected()}
                  isIndeterminate={table.getIsSomeRowsSelected()}
                  onChange={table.getToggleAllRowsSelectedHandler()}
                />
              </div>
            ),
            cell: ({ row }) => (
              <div>
                <Checkbox
                  checked={row.getIsSelected()}
                  disabled={!row.getCanSelect()}
                  onChange={row.getToggleSelectedHandler()}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            ),
          } as ColumnDef<any>),

        // Expanding Icon Column
        !!subComponentKey &&
          ({
            id: "expander",
            header: () => null,
            cell: ({ row }) =>
              row.getCanExpand() ? (
                <button
                  {...{
                    onClick: (e) => {
                      e.stopPropagation();
                      row.getToggleExpandedHandler()();
                    },
                  }}
                >
                  <ChevronDownIcon
                    className={cn(
                      "h-5 w-5 text-foundations-subtle transition-transform duration-300",
                      row.getIsExpanded() ? "" : "-rotate-90"
                    )}
                  />
                </button>
              ) : null,
          } as ColumnDef<any>),

        // BE Columns
        ...columnsData,

        // Sticky action columns
        (getPreviewPath || editEnabled || deleteEnabled) &&
          ({
            id: "option",
            header: () => <></>,
            meta: {
              className:
                "bg-foundations-default sticky right-0 group-data-[scroll-at-end='false']:before:absolute group-data-[scroll-at-end='false']:before:border-l group-data-[scroll-at-end='false']:before:border-l-foundations-subtle group-data-[scroll-at-end='false']:before:bg-red-400 group-data-[scroll-at-end='false']:before:left-0 group-data-[scroll-at-end='false']:before:inset-y-0 [th&]:sm:rounded-tr-lg [tr:last-child_&]:sm:rounded-br-lg [tr:last-child_&]:border-l-foundations-subtle",
            },
            cell: ({ row }) => (
              <ButtonActionGroup
                formURL={formURL!}
                tableName={tableName}
                row={row}
                previews={previewsData}
                getPreviewPath={getPreviewPath}
                editEnabled={editEnabled!}
                deleteEnabled={deleteEnabled!}
                onEditItemClick={onEditItemClick!}
                onDeleteItemClick={(id) => {
                  onDeleteItemClick
                    ? onDeleteItemClick(id)
                    : doBulkAction(null, "delete_selected", id);
                }}
                refreshTable={refreshTable}
                showSuccessMessage={showSuccessMessage as any}
              />
            ),
          } as ColumnDef<any>),
      ].filter(Boolean) as ColumnDef<any>[];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      columnsData,
      disableBulkActions,
      bulkActions?.length,
      editEnabled,
      deleteEnabled,
    ]);

    const tableInstance = useReactTable({
      columns,
      data: rowsData,
      getRowId: (row: any) => getRowId?.(row) || row?.id || row?.ui_hidden_id,
      state: {
        columnVisibility,
      },
      getCoreRowModel: getCoreRowModel(),
      getRowCanExpand: () => !!subComponentKey,
      getExpandedRowModel: getExpandedRowModel(),
      manualSorting: true,
      manualPagination: true,
      manualFiltering: true,
      rowCount: dataCount,
    });

    const rows = tableInstance.getRowModel().rows;

    const selectedRowsById = tableInstance.getSelectedRowModel().rowsById;
    const selectedRows = tableInstance.getSelectedRowModel().flatRows;
    const hasSelectedRows = selectedRows.length > 0;

    // Reset bulk actions error rows & message on-deselect
    useEffect(() => {
      setBulkActionErrorIds((prevErrors) =>
        prevErrors.filter((rowId) => selectedRowsById[rowId])
      );
    }, [selectedRowsById]);

    function doBulkAction(method: any, type: any, singleID?: any) {
      const { _offset, _limit, ...tableFiltersWithoutOffsetLimit } = query;
      setBulkActionErrorIds([]);

      const selectedIDs: any = selectedRows.map((row) => row.id);
      const _selectedIDs = singleID ? [singleID] : selectedIDs;

      axiosApiInstance
        .post(
          `${url}bulk_action/`,
          {
            bulk_action: type,
            ...(type !== exportBulkActionNames[1] && {
              selected: _selectedIDs,
            }),
          },
          { params: tableFiltersWithoutOffsetLimit }
        )
        .then(function () {
          showSuccessMessage({ name: type } as any);

          if (type === "delete_selected") {
            const rowsDataAfterDelete = rowsData.filter(
              ({ id }) => _selectedIDs.indexOf(id) === -1
            );
            setRowsData(rowsDataAfterDelete);
            refreshTable();
            return;
            // FIXME: https://app.clickup.com/t/861m990u6
            // These cases should be moved from here
          } else if (exportBulkActionNames.includes(type)) {
            // NOTE: Only download csv files

            return;
          }

          refreshTable();
        })
        .catch(function (err) {
          const [errorMessage] = parseErrorsToArray(err);
          toast.error(errorMessage);
        });
    }

    function refreshTable() {
      refetch();
    }

    function applySort(column: any) {
      let newSortValue;
      // sort desc first
      if (!tableSorts[column]) {
        newSortValue = "desc";
      } // sort asc second time
      else if (tableSorts[column] === "desc") {
        newSortValue = "asc";
      } // reset sorting
      if (tableSorts[column] === "asc") {
        const { ordering: _ordering, ...restQuery } = query;
        router.replace({ query: restQuery });
        return;
      }

      router.replace({
        query: {
          ...router.query,
          ordering: `${newSortValue === "desc" ? "-" : ""}${column}`,
        },
      });
    }

    function removeAllFilters() {
      router.replace({ query: { page: 1 } });
    }

    function showSuccessMessage({ name, action, message }: any) {
      const id = `${name}-${action}-${message}`;
      if (message) {
        toast.success(message, { id });
        return;
      }

      if (action) {
        toast.success(
          `${tableName} ${name ? `"${name}"` : ""} was successfully ${action}.`,
          {
            id,
          }
        );
        return;
      }

      if (!(SUCCESS_MESSAGES as any)[name]) return;

      toast.success((SUCCESS_MESSAGES as any)[name](tableName), { id });
    }

    const isFiltering = useMemo(
      () =>
        Boolean(
          searchQuery ||
            (filtersEnabled &&
              filters?.some(({ field_name }) => !!router.query[field_name]))
        ),
      [searchQuery, filtersEnabled, filters, router.query]
    );

    const showTableHead =
      !hideHeader &&
      (tableName ||
        tableDescription ||
        filtersEnabled ||
        addEnabled ||
        listMetaEnableSearch);

    useImperativeHandle<any, any>(ref, () => ({
      refreshTable,
    }));

    const { showSearch, showFilters } = useMemo(() => {
      const showFilters =
        filtersEnabled && Object.keys(filters!).length > 0 && !hideFilters;
      const showSearch = listMetaEnableSearch && !hideGlobalSearch;
      return {
        showFilters,
        showSearch,
      };
    }, [
      filters,
      filtersEnabled,
      hideFilters,
      listMetaEnableSearch,
      hideGlobalSearch,
    ]);

    return (
      <div className="flex w-full flex-col gap-4" ref={ref}>
        {showTableHead && (
          <div className="flex flex-col gap-4">
            <PageHeader
              title={
                tableTitle ||
                `Manage ${
                  tableName?.charAt(0).toUpperCase() + tableName?.slice(1)
                }s`
              }
              description={tableDescription}
              withoutBorder={!!navigationTabs}
              actions={
                <div className="flex flex-row flex-wrap items-center justify-end gap-2">
                  {addEnabled ? (
                    onAddItemClick ? (
                      addItemContent ? (
                        addItemContent
                      ) : (
                        <IconButton onClick={onAddItemClick}>
                          <PlusIcon className="h-4 w-4" aria-hidden="true" />
                        </IconButton>
                      )
                    ) : (
                      <AddPopOverForm
                        open={addOpen}
                        setOpen={setAddOpen}
                        url={formURL!}
                        entityName={tableName}
                        title={`New ${tableName}`}
                        onSuccess={(name, data) => {
                          onAddItemSuccessfully?.(data);
                          showSuccessMessage({ name, action: "added" });
                          refreshTable();
                          router.replace({
                            query: {
                              ...router.query,
                              preview: data?.preview?.id,
                            },
                          });
                        }}
                      />
                    )
                  ) : null}
                  {CustomTopButton && (
                    <CustomTopButton
                      dataCount={data?.tableData?.length}
                      doBulkAction={doBulkAction}
                    />
                  )}
                </div>
              }
            />

            {navigationTabs}
          </div>
        )}

        {isFetchingError ? <ErrorPage error={error} /> : null}
        {!isFetchingError && (
          <>
            {/* TABLE */}
            {isLoading && tableData.length === 0 ? (
              <TableSkeleton
                rows={+paginationParams.limit}
                withToolbar={!hideFilters || !hideGlobalSearch}
              />
            ) : (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  {tableData.length > 0 && (
                    <p className="text-sm font-medium text-foundations-bold">
                      {tableData.length <= dataCount!
                        ? tableData.length
                        : dataCount}{" "}
                      of {dataCount} {tableName}s
                    </p>
                  )}

                  {/* NOTE: Always show the filters even if there is no items, so users can adjust their filters */}
                  {(showFilters || showSearch) && (
                    <Filters
                      leftSlot={
                        tableView && (
                          <div data-slot="table-view">{tableView}</div>
                        )
                      }
                      filters={filters}
                      enabledFilters={showFilters}
                      recentStoreKey={tableName}
                      datePickerMode={filtersProps?.datePickerMode}
                      dateRangeMode={filtersProps?.dateRangeMode}
                      nonClearableFilters={filtersProps?.nonClearableFilters}
                      search={{
                        enabled: !!showSearch,
                        placeholder: `Search ${tableName}s`,
                      }}
                    />
                  )}
                </div>

                {!tableData.length || !columnsData.length ? (
                  <NoResults
                    searchEnabled={listMetaEnableSearch}
                    hasSearchQuery={!!searchQuery}
                    isFiltering={isFiltering}
                    addEnabled={addEnabled}
                    tableName={tableName}
                    emptyMessage={emptyMessage}
                    removeAllFilters={removeAllFilters}
                    onAddItemClick={onAddItemClick}
                    setAddOpen={setAddOpen}
                  />
                ) : (
                  <>
                    {Object.keys(summary).length > 0 && TableSummary && (
                      <TableSummary summary={summary} />
                    )}

                    {TableAlert && <TableAlert />}

                    <Table
                      previews={previewsData}
                      customPreview={customPreview}
                      editEnabled={!!editEnabled}
                      tableName={tableName}
                      formURL={formURL!}
                      applySort={applySort}
                      tableSorts={tableSorts}
                      refreshTable={refreshTable}
                      onRowClick={onRowClick}
                      getPreviewPath={getPreviewPath}
                      enableRowClickNavigation={enableRowClickNavigation}
                      onEditItemClick={onEditItemClick}
                      tableInstance={tableInstance}
                      bulkActionErrorIds={bulkActionErrorIds}
                      showSuccessMessage={showSuccessMessage}
                      isLoading={isLoading}
                      previewMode={previewMode}
                      getRowId={getRowId}
                      subComponentRender={subComponentRender}
                    />
                  </>
                )}
              </div>
            )}
            {/* End: TABLE */}
          </>
        )}

        {/* PAGINATION */}
        <Pagination
          total={dataCount!}
          onPageChange={({ page, limit }) => {
            router.replace({ query: { ...router.query, page, limit } });
          }}
          defaultLimit={paginationDefaultLimit}
        />

        {/* Table Actions */}

        <TableActions
          hasSelectedRows={hasSelectedRows}
          table={tableInstance}
          BulkActionsComponent={BulkActionsComponent}
          bulkActions={bulkActions!}
          bulkActionsPermissions={bulkActionsPermissions}
          tableName={tableName}
          doBulkAction={doBulkAction}
          rows={rows}
          previews={previewsData}
          dataCount={dataCount!}
          pagesCount={pagesCount}
          setBulkActionErrorIds={setBulkActionErrorIds}
          showSuccessMessage={showSuccessMessage}
          refreshTable={refreshTable}
        />
      </div>
    );
  }
);

TableContainer.displayName = "TableContainer";

export default TableContainer;

const TableActions = ({
  hasSelectedRows,
  table,
  BulkActionsComponent,
  bulkActions,
  bulkActionsPermissions,
  tableName,
  doBulkAction,
  rows,
  previews: previewsData,
  dataCount,
  pagesCount,
  setBulkActionErrorIds,
  showSuccessMessage,
  refreshTable,
}: {
  hasSelectedRows: boolean;
  table: TableType<any>;
  BulkActionsComponent?: React.ElementType;
  bulkActions: { name: string; type: string }[];
  bulkActionsPermissions: Record<string, boolean>;
  tableName: string;
  doBulkAction: (method: any, type: any) => void;
  rows: Row<any>[];
  previews: any[];
  dataCount: number;
  pagesCount: number;
  setBulkActionErrorIds: React.Dispatch<React.SetStateAction<string[]>>;
  showSuccessMessage: (options: {
    name: string;
    action?: string;
    message?: string;
  }) => void;
  refreshTable: () => void;
}) => {
  const selectedRows = table.getSelectedRowModel().flatRows;
  const isIndeterminate = table.getIsSomeRowsSelected();

  return (
    <div
      className={cn(
        "transition-all duration-300",
        "fixed -bottom-[500px] self-center",
        hasSelectedRows && "bottom-28",
        "flex min-w-80 items-center justify-between gap-4 rounded-lg border-2 border-info-bold bg-foundations-inverse p-4 shadow-xl"
      )}
    >
      {/* Toggle all checkbox */}
      <div className="flex shrink-0 items-center gap-2">
        <Tooltip>
          <Tooltip.Trigger
            delay={0}
            render={
              <Checkbox
                checked={table.getIsAllRowsSelected()}
                isIndeterminate={isIndeterminate}
                onChange={table.getToggleAllRowsSelectedHandler()}
              />
            }
          />
          <Tooltip.Popup size="sm" variant="light">
            {isIndeterminate ? "Select All" : "Deselect All"}
          </Tooltip.Popup>
        </Tooltip>

        <span className="text-sm font-medium text-foundations-inverse">
          {selectedRows.length === dataCount ? "All" : selectedRows.length}{" "}
          selected
        </span>
      </div>

      {/* Actions Menu */}
      {!BulkActionsComponent && !!bulkActions && (
        <Menu>
          <Menu.Trigger className="group flex items-center gap-2 rounded border border-foundations-bold bg-foundations-default p-2">
            <span className="text-sm text-foundations-bolder">Actions</span>

            <ChevronDownIcon
              className="ml-auto h-4 w-4 shrink-0 text-foundations-subtler group-data-[popup-open]:text-info"
              aria-hidden="true"
            />
          </Menu.Trigger>
          <Menu.Popup align="end" side="top" sideOffset={8} className="py-1">
            {bulkActions.map((action, index) => (
              <ButtonWithConfirmModal
                key={index}
                label={action.name}
                type={action.type}
                hasPermission={(bulkActionsPermissions as any)[action.type]}
                tableName={tableName}
                rowsCount={selectedRows.length}
                dataCount={dataCount!}
                onConfirm={({ method, type }) => doBulkAction(method, type)}
                onDeselectAll={() => table.resetRowSelection(true)}
              />
            ))}
          </Menu.Popup>
        </Menu>
      )}

      {/* Export all button */}
      {!!BulkActionsComponent && (
        <BulkActionsComponent
          doBulkAction={doBulkAction as any}
          bulkActionsPermissions={bulkActionsPermissions}
          rows={rows}
          previews={previewsData}
          selectedRows={table.getSelectedRowModel().flatRows}
          dataCount={dataCount}
          pagesCount={pagesCount}
          onDeselectAll={() => table.resetRowSelection(true)}
          setBulkActionErrorIds={setBulkActionErrorIds}
          showSuccessMessage={showSuccessMessage}
          refreshTable={refreshTable}
        />
      )}
    </div>
  );
};
